import { Component, OnInit } from '@angular/core';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Router, ActivatedRoute } from '@angular/router';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { ConfigurationService } from 'src/app/private/services/configuration.service';
import { TabViewModule } from 'primeng/tabview';
import { EmailHubService } from 'src/app/private/services/email-hub.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  selector: 'app-configuration-panel',
  standalone: true,
  imports: [SharedModule, TabViewModule, ConfirmDialogModule],
  templateUrl: './configuration-panel.component.html',
  styleUrl: './configuration-panel.component.scss',
  providers: [MessageService, ConfirmationService]
})
export class ConfigurationPanelComponent implements OnInit {
  activeTab: number = 0;
  notificationTab: number = 0;

  // Notification Configuration
  organizationConfig: any = {};
  authorizedDomains: any[] = [];
  selectedDomain: any = null;
  previousSelectedDomain: any = null;
  allNotificationEmails: string[] = []; // All emails from all domains combined (plain strings for dropdowns)
  emailsWithMetrics: any[] = []; // Email objects with optional reputation data
  pendingDeleteEmails: string[] = []; // Saved emails marked for deletion before next save
  newEmail: string = '';
  loadingDomains: boolean = false;
  loadingConfig: boolean = false;
  initialLoading: boolean = true;
  savingEmails: boolean = false;
  savingModules: boolean = false;

  isGmailTransparentLoading: boolean = false;
  isOutlookTransparentLoading: boolean = false;

  // Temporary storage for emails per domain (before saving)
  tempEmailsByDomain: { [domainId: string]: string[] } = {};
  tempModuleMappingByDomain: { [domainId: string]: any } = {};

  // Module email assignments
  modules = [
    // { name: 'Leads', key: 'leads', assignedEmail: null },
    // { name: 'Registration', key: 'registration', assignedEmail: null },
    // { name: 'Admission', key: 'admission', assignedEmail: null },
    // { name: 'Application', key: 'application', assignedEmail: null },
    // { name: 'Ticket', key: 'ticket', assignedEmail: null },
    // { name: 'Enrollment', key: 'enrollment', assignedEmail: null },
    // { name: 'Student', key: 'student', assignedEmail: null },
    // { name: 'Student Ticket', key: 'student_ticket', assignedEmail: null },
   {
  name: 'Marketing Campaign',
  key: 'marketing_campaign',
  assignedEmail: null,
  description: 'Used for marketing and promotional emails such as campaigns and product updates. Only approved domain-based sender emails (e.g., sales@domain.com, marketing@domain.com) are allowed to ensure deliverability and reduce spam risk.'
},
{
  name: 'Student Profile Credentials',
  key: 'student_profile_credentials',
  assignedEmail: null,
  description: 'Used for transactional student emails including login credentials, account activation, and profile access details.'
}

  ];

  // Communication Configuration
  communicationTab: number = 0;
  outlookConnected: boolean = false;
  outlookStatus: string = 'not_connected'; // 'connected', 'expired', 'not_connected'
  gmailConnected: boolean = false;
  gmailStatus: string = 'not_connected'; // 'connected', 'expired', 'not_connected'
  connectedOutlookEmail: string = '';
  connectedGmailEmail: string = '';
  outlookExpiresAt: string = '';
  gmailExpiresAt: string = '';
  isConnectLoading: boolean = false;
  isGmailConnectLoading: boolean = false;
  revokeLoading: boolean = false;
  gmailRevokeLoading: boolean = false;  

  // WhatsApp Configuration
  whatsappConfig :any = {
    email: '',
    app_id: '',
    source: '',
    api_key: '',
    app_name: '',
    password: '',
    app_token: ''
  };
  savingWhatsApp: boolean = false;

  constructor(
    private configService: ConfigurationService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private localStorage: LocalStorageService,
    private api: EmailHubService,
    private confirmationService: ConfirmationService
  ) { }

  ngOnInit() {
    // Check for query params to set active tab
    this.route.queryParams.subscribe(params => {
      if (params['activeTab']) {
        this.activeTab = parseInt(params['activeTab'], 10);
      }
    });
    
    this.loadAuthorizedDomains();
    this.getOrganizationConfigurations();
    this.checkOutlookConnectionReq();
    this.checkGmailConnectionReq();
  }

  checkOutlookConnectionReq() {
    const body = {
      organization_id: this.localStorage.getOrganizationId(),
      user_id: this.localStorage.getUserId()
    };

    this.configService.checkOutlookConnection(body).subscribe({
      next: (response: any) => {
        if (response.status === 'connected') {
          this.outlookConnected = true;
          this.outlookStatus = 'connected';
          this.connectedOutlookEmail = response.email;
          this.outlookExpiresAt = response.expires_at;
          this.localStorage.setDefaultEmailCommunicationMode('outlook');
        } else if (response.status === 'expired') {
          this.outlookConnected = false;
          this.outlookStatus = 'expired';
          this.connectedOutlookEmail = response.email;
        } else {
          this.outlookConnected = false;
          this.outlookStatus = 'not_connected';
          this.connectedOutlookEmail = '';

        }
      },
      error: (error) => {
        console.error('Error checking Outlook connection:', error);
        this.outlookConnected = false;
        this.outlookStatus = 'not_connected';
      }
    });
  }

  checkGmailConnectionReq() {
    const body = {
      organization_id: this.localStorage.getOrganizationId(),
      user_id: this.localStorage.getUserId()
    };

    this.configService.checkGmailConnection(body).subscribe({
      next: (response: any) => {
        this.isGmailTransparentLoading = false;
        if (response.connected) {
          this.gmailConnected = true;
          this.gmailStatus = 'connected';
          this.connectedGmailEmail = response.email;
          this.gmailExpiresAt = response.expires_at;
          this.localStorage.setDefaultEmailCommunicationMode('gmail');
        } else if (response.status === 'expired') {
          this.gmailConnected = false;
          this.gmailStatus = 'expired';
          this.connectedGmailEmail = response.email;
        } else {
          this.gmailConnected = false;
          this.gmailStatus = 'not_connected';
          this.connectedGmailEmail = '';
        }
      },
      error: (error) => {
        this.isGmailTransparentLoading = false;
        console.error('Error checking Gmail connection:', error);
        this.gmailConnected = false;
        this.gmailStatus = 'not_connected';
      }
    });
  }


  checkForConnectedOutlookEmail() {
  }

  // ============ Notification Configuration Methods ============

  loadAuthorizedDomains() {
    this.loadingDomains = true;

    const body: any = {
      "organization_id": this.localStorage.getOrganizationId(),
    }
    this.configService.getAllClientDomain(body).subscribe({
      next: (response: any) => {
        this.authorizedDomains = response;
        // filter out status Success
        this.authorizedDomains = this.authorizedDomains.filter((domain: any) => domain.status == 'Success');
        this.loadingDomains = false;
        this.checkInitialLoadingComplete();
      },
      error: (error) => {
        console.error('Error loading domains:', error);
        this.loadingDomains = false;
        this.checkInitialLoadingComplete();
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load authorized domains'
        });
      }
    });
  }


  getOrganizationConfigurations() {
    this.loadingConfig = true;
    const body: any = {
      "organization_id": this.localStorage.getOrganizationId()
    }

    // Load Communication Configuration
    this.configService.getOrganizationConfig(body).subscribe({
      next: (response: any) => {
        this.organizationConfig = response;
        this.refreshAllEmailsDisplay();
        
        // Load WhatsApp config if exists
        if (response?.whats_app_config) {
          this.whatsappConfig = {
            email: response.whats_app_config.email || '',
            app_id: response.whats_app_config.app_id || '',
            source: response.whats_app_config.source || '',
            api_key: response.whats_app_config.api_key || '',
            app_name: response.whats_app_config.app_name || '',
            password: response.whats_app_config.password || '',
            app_token: response.whats_app_config.app_token || ''
          };
        }
        
        this.loadingConfig = false;
        this.checkInitialLoadingComplete();
      },
      error: (error) => {
        console.error('Error loading organization configurations:', error);
        this.loadingConfig = false;
        this.checkInitialLoadingComplete();
      }
    });
  }

  checkInitialLoadingComplete() {
    if (!this.loadingDomains && !this.loadingConfig) {
      this.initialLoading = false;
    }
  }

  refreshAllEmailsDisplay() {
    // Use emails_with_metrics from saved config (includes reputation data)
    const savedEmailsWithMetrics: any[] = this.organizationConfig?.emails_with_metrics || [];

    // Filter out emails marked for pending delete
    const filteredSaved = savedEmailsWithMetrics.filter(
      (e: any) => !this.pendingDeleteEmails.includes(e.email)
    );
    const savedEmailStrings = filteredSaved.map((e: any) => e.email);

    const combined: any[] = [...filteredSaved];

    // Add emails from temp storage (pending/unsaved — no metrics yet)
    Object.keys(this.tempEmailsByDomain).forEach(domainId => {
      const emails = this.tempEmailsByDomain[domainId];
      if (emails && emails.length > 0) {
        emails.forEach((email: string) => {
          if (!savedEmailStrings.includes(email) && !combined.find(e => e.email === email)) {
            combined.push({ email, _isPending: true });
          }
        });
      }
    });

    this.emailsWithMetrics = combined;
    this.allNotificationEmails = combined.map(e => e.email);

    // Load module mappings from saved config
    const moduleMapping = this.organizationConfig?.module_mapping;
    if (moduleMapping) {
      this.modules.forEach(module => {
        module.assignedEmail = moduleMapping[module.key] || null;
      });
    }
  }

  getReputationScoreClass(score: string | undefined): string {
    if (!score) return 'bg-gray-100 text-gray-500';
    const val = parseFloat(score);
    if (val >= 80) return 'bg-green-100 text-green-700';
    if (val >= 60) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  }

  getReputationScoreBorderClass(score: string | undefined): string {
    if (!score) return 'border-gray-300';
    const val = parseFloat(score);
    if (val >= 80) return 'border-green-400';
    if (val >= 60) return 'border-yellow-400';
    return 'border-red-400';
  }

  navigateToDomainAuth() {
    this.router.navigate(['/main/settings/configuration-domain-authentication']);
  }

  onDomainChange() {
    // Just update the previous selected domain for tracking
    if (this.selectedDomain) {
      this.previousSelectedDomain = this.selectedDomain;
    }
    // No need to load/unload emails since we show all emails anyway
  }

  addNotificationEmailLocal() {
    if (!this.newEmail || !this.selectedDomain) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please enter an email address and select a domain'
      });
      return;
    }

    const fullEmail = `${this.newEmail}@${this.selectedDomain.domain}`;

    // Check for duplicates across all emails
    if (this.allNotificationEmails.includes(fullEmail)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Email already exists'
      });
      return;
    }

    // Add to temp storage for this domain
    if (!this.tempEmailsByDomain[this.selectedDomain.id]) {
      this.tempEmailsByDomain[this.selectedDomain.id] = [];
    }
    this.tempEmailsByDomain[this.selectedDomain.id].push(fullEmail);

    this.newEmail = '';
    this.refreshAllEmailsDisplay();

    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Email added to list. Click Save to update.'
    });
  }

  deleteNotificationEmailLocal(index: number) {
    const emailToDelete = this.emailsWithMetrics[index]?.email;
    if (!emailToDelete) return;

    // Try to remove from temp storage (pending unsaved emails)
    let removedFromTemp = false;
    Object.keys(this.tempEmailsByDomain).forEach(domainId => {
      const emails = this.tempEmailsByDomain[domainId];
      const emailIndex = emails.indexOf(emailToDelete);
      if (emailIndex > -1) {
        emails.splice(emailIndex, 1);
        if (emails.length === 0) {
          delete this.tempEmailsByDomain[domainId];
        }
        removedFromTemp = true;
      }
    });

    // If it's a saved email, mark for pending delete (will be excluded on next save)
    if (!removedFromTemp) {
      this.pendingDeleteEmails.push(emailToDelete);
    }

    this.refreshAllEmailsDisplay();

    this.messageService.add({
      severity: 'info',
      summary: 'Removed',
      detail: 'Email removed from list. Click Save to update.'
    });
  }

  saveEmailConfiguration() {
    if (this.allNotificationEmails.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please add at least one email'
      });
      return;
    }

    this.savingEmails = true;

    // Collect all emails (from temp and saved)
    const allEmailsSet: Set<string> = new Set(this.allNotificationEmails);

    const moduleMapping: any = {};
    this.modules.forEach(module => {
      if (module.assignedEmail) {
        moduleMapping[module.key] = module.assignedEmail;
      }
    });

    const payload : any = {
      organization_id: this.localStorage.getOrganizationId(),
      email_config: {
        emails: Array.from(allEmailsSet)
      },
      module_mapping: moduleMapping
    };

    console.log('Save Payload:', payload);

    if (this.organizationConfig?.id) {

      payload.id = this.organizationConfig.id;

      // Update existing config
      this.configService.updateOrganizationConfig(payload).subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Email configuration saved successfully'
          });
          this.savingEmails = false;

          // Clear all temp storage and pending deletes after successful save
          this.tempEmailsByDomain = {};
          this.pendingDeleteEmails = [];

          // update localstorage module_mapping
          localStorage.setItem('module_mapping', JSON.stringify(moduleMapping));

          this.getOrganizationConfigurations();
        },
        error: (error) => {
          console.error('Error saving email configuration:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save email configuration'
          });
          this.savingEmails = false;
        }
      });
    } else {
      // create Organization Config first
      this.configService.createOrganizationConfig(payload).subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Email configuration saved successfully'
          });
          this.savingEmails = false;

          // Clear all temp storage and pending deletes after successful save
          this.tempEmailsByDomain = {};
          this.pendingDeleteEmails = [];

          this.organizationConfig = response.data;

          this.getOrganizationConfigurations();
        },
        error: (error) => {
          console.error('Error saving email configuration:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save email configuration'
          });
          this.savingEmails = false;
        }
      });
    }
  }

  saveModuleMapping() {
    if (this.allNotificationEmails.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please add notification emails first'
      });
      return;
    }

    this.savingModules = true;

    const moduleMapping: any = {};
    this.modules.forEach(module => {
      if (module.assignedEmail) {
        moduleMapping[module.key] = module.assignedEmail;
      }
    });

    const payload = {
      organization_id: this.localStorage.getOrganizationId(),
      module_mapping: moduleMapping
    };

    this.configService.updateOrganizationConfig(payload).subscribe({
      next: (response: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Module mapping saved successfully'
        });
        this.savingModules = false;

        // Clear temp storage for module mappings
        this.tempModuleMappingByDomain = {};

        this.getOrganizationConfigurations();
      },
      error: (error) => {
        console.error('Error saving module mapping:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save module mapping'
        });
        this.savingModules = false;
      }
    });
  }

  // ============ Communication Configuration Methods ============

  checkOutlookConnection() {
    const body = {
      organization_id: this.localStorage.getOrganizationId(),
      user_id: this.localStorage.getUserId()
    };

    this.configService.checkOutlookConnection(body).subscribe({
      next: (response: any) => {
        this.isOutlookTransparentLoading = false;
        if (response.status === 'connected') {
          this.outlookConnected = true;
          this.outlookStatus = 'connected';
          this.connectedOutlookEmail = response.email;
          this.outlookExpiresAt = response.expires_at;
          this.localStorage.setDefaultEmailCommunicationMode('outlook');
        } else if (response.status === 'expired') {
          this.outlookConnected = false;
          this.outlookStatus = 'expired';
          this.connectedOutlookEmail = response.email;
        } else {
          this.outlookConnected = false;
          this.outlookStatus = 'not_connected';
          this.connectedOutlookEmail = '';
        }
      },
      error: (error) => {
        this.isOutlookTransparentLoading = false;
        console.error('Error checking Outlook connection:', error);
        this.outlookConnected = false;
        this.outlookStatus = 'not_connected';
      }
    });
  }


  
  connectOutlook() {
    // Check if Gmail is already connected
    if (this.gmailConnected && this.gmailStatus === 'connected') {
      this.confirmationService.confirm({
        message: `You can only connect one communication medium at a time. Connecting Outlook will automatically logout from Gmail (${this.connectedGmailEmail}). Do you want to continue?`,
        header: 'Communication Medium Warning',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Yes, Continue',
        rejectLabel: 'Cancel',
        acceptButtonStyleClass: 'p-button-warning',
        rejectButtonStyleClass: 'p-button-outlined',
        accept: () => {
          this.proceedWithOutlookConnection(true);
        },
        reject: () => {

          this.confirmationService.close();
        }
      });
      return;
    }

    // Proceed without removing Gmail
    this.proceedWithOutlookConnection(false);
  }

  proceedWithOutlookConnection(removeGoogle: boolean) {
    this.isConnectLoading = true;

    const payload: any = {
      organization_id: this.localStorage.getOrganizationId(),
      email: this.localStorage.getEmail(),
      user_id: this.localStorage.getUserId()
    };

    // Add removeGoogle flag if Gmail is connected
    if (removeGoogle) {
      payload.removeGoogle = true;
    }

    this.api.getCodeForOutlookToken(payload).subscribe({
      next: (res: any) => {
        this.isConnectLoading = false;
        this.confirmationService.close();
        const url = res?.message;
        if (url) {
          // Open popup with specific size
          const popup = window.open(
            url,
            'OutlookConnectWindow',
            'width=600,height=700,left=200,top=100'
          );

          if (popup) {
            // Polling to check if popup is closed
            const timer = setInterval(() => {
              if (popup.closed) {
                clearInterval(timer);
                console.log('Popup window closed');
                this.onPopupClosed();  // 🔁 Call your desired function here
              }
            }, 500);
          } else {
            console.error('Failed to open popup window. It might be blocked by the browser.');
          }
        } else {
          console.error('No URL found in response:', res);
        }
      },
      error: (err: any) => {
        this.isConnectLoading = false;
        console.error('Error fetching code for Outlook token:', err);
      }
    });
  }

    onPopupClosed() {
    // this.initLoading = true;
    // this.isAccountNotConnected = false;
    // window.location.reload();
    this.isOutlookTransparentLoading = true;
    this.isGmailTransparentLoading = true;
    this.checkOutlookConnection();
    this.checkGmailConnectionReq();
    // this.childComponent.refetch();
  }

  revokeOutlookAccess() {
    const payload = {
      // email: this.localStorage.getEmail(),
      organization_id: this.localStorage.getOrganizationId(),
      user_id: this.localStorage.getUserId()
    };
    
    this.revokeLoading = true;
    this.configService.revokeAccess(payload).subscribe({
      next: (res) => {
        this.revokeLoading = false;
        this.messageService.add({ 
          severity: 'success', 
          summary: 'Success', 
          detail: 'Access revoked successfully' 
        });
        this.isOutlookTransparentLoading = true;
        this.localStorage.setDefaultEmailCommunicationMode('none');
        this.checkOutlookConnection();
      },
      error: (e) => {
        this.revokeLoading = false;
        console.error(e);
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Failed to revoke access' 
        });
      }
    });
  }

  connectGmail() {
    // Check if Outlook is already connected
    if (this.outlookConnected && this.outlookStatus === 'connected') {
      this.confirmationService.confirm({
        message: `You can only connect one communication medium at a time. Connecting Gmail will automatically logout from Outlook (${this.connectedOutlookEmail}). Do you want to continue?`,
        header: 'Communication Medium Warning',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Yes, Continue',
        rejectLabel: 'Cancel',
        acceptButtonStyleClass: 'p-button-warning',
        rejectButtonStyleClass: 'p-button-outlined',
        accept: () => {
          this.proceedWithGmailConnection(true);
        },
        reject: () => {
          this.confirmationService.close();
        }
      });
      return;
    }

    // Proceed without removing Outlook
    this.proceedWithGmailConnection(false);
  }

  proceedWithGmailConnection(removeOutlook: boolean) {
    this.isGmailConnectLoading = true;

    const payload: any = {
      organization_id: this.localStorage.getOrganizationId(),
      email: this.localStorage.getEmail(),
      user_id: this.localStorage.getUserId()
    };

    // Add removeOutlook flag if Outlook is connected
    if (removeOutlook) {
      payload.removeOutlook = true;
    }

    this.api.getCodeForGmailToken(payload).subscribe({
      next: (res: any) => {
        this.confirmationService.close();
        this.isGmailConnectLoading = false;
        const url = res?.message;
        if (url) {
          // Open popup with specific size
          const popup = window.open(
            url,
            'GmailConnectWindow',
            'width=600,height=700,left=200,top=100'
          );

          if (popup) {
            // Polling to check if popup is closed
            const timer = setInterval(() => {
              if (popup.closed) {
                clearInterval(timer);
                console.log('Popup window closed');
                this.onGmailPopupClosed();
              }
            }, 500);
          } else {
            console.error('Failed to open popup window. It might be blocked by the browser.');
          }
        } else {
          console.error('No URL found in response:', res);
        }
      },
      error: (err: any) => {
        this.isGmailConnectLoading = false;
        console.error('Error fetching code for Gmail token:', err);
      }
    });
  }

  onGmailPopupClosed() {
    this.isGmailTransparentLoading = true;
    this.isOutlookTransparentLoading = true;
    this.checkGmailConnectionReq();
    this.checkOutlookConnection();
  }

  disconnectOutlook() {
    this.configService.disconnectOutlook({}).subscribe({
      next: () => {
        this.outlookConnected = false;
        this.connectedOutlookEmail = '';
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Outlook disconnected successfully'
        });
      },
      error: (error) => {
        console.error('Error disconnecting Outlook:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to disconnect Outlook'
        });
      }
    });
  }

  revokeGmailAccess() {
    const payload = {
      // email: this.localStorage.getEmail(),
      organization_id: this.localStorage.getOrganizationId(),
      user_id: this.localStorage.getUserId()
    };
    
    this.gmailRevokeLoading = true;

    this.configService.disconnectGmail(payload).subscribe({
      next: (res) => {
        this.gmailRevokeLoading = false;
        this.messageService.add({ 
          severity: 'success', 
          summary: 'Success', 
          detail: 'Gmail access revoked successfully' 
        });
        this.isGmailTransparentLoading = true;
        this.localStorage.setDefaultEmailCommunicationMode('none');
        this.checkGmailConnectionReq();
      },
      error: (e) => {
        this.gmailRevokeLoading = false;
        console.error(e);
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Failed to revoke Gmail access' 
        });
      }
    });
  }

  // ============ WhatsApp Configuration Methods ============

  loadWhatsAppConfiguration() {
    this.configService.getWhatsAppConfig({}).subscribe({
      next: (response: any) => {
        if (response.data) {
          this.whatsappConfig = {
            apiKey: response.data.apiKey || '',
            appName: response.data.appName || '',
            sourceNumber: response.data.sourceNumber || '',
            userId: response.data.userId || ''
          };
        }
      },
      error: (error) => {
        console.error('Error loading WhatsApp config:', error);
      }
    });
  }

  saveWhatsAppConfiguration() {
    if (!this.whatsappConfig.email || !this.whatsappConfig.api_key || 
        !this.whatsappConfig.app_name || !this.whatsappConfig.source) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please fill all required fields (Email, API Key, App Name, Source)'
      });
      return;
    }

    this.savingWhatsApp = true;

    const payload: any = {
      ...this.organizationConfig,
      organization_id: this.localStorage.getOrganizationId(),
      whats_app_config: this.whatsappConfig
    };

    if (this.organizationConfig?.id) {
      payload.id = this.organizationConfig.id;

      // Update existing config
      this.configService.updateOrganizationConfig(payload).subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'WhatsApp configuration saved successfully'
          });
          this.savingWhatsApp = false;
          // Update localStorage to indicate WhatsApp configuration exists
          this.localStorage.setIsWhatsappExist(true);
          this.getOrganizationConfigurations();
        },
        error: (error) => {
          console.error('Error saving WhatsApp config:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save WhatsApp configuration'
          });
          this.savingWhatsApp = false;
        }
      });
    } else {
      // Create organization config
      this.configService.createOrganizationConfig(payload).subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'WhatsApp configuration saved successfully'
          });
          this.savingWhatsApp = false;
          // Update localStorage to indicate WhatsApp configuration exists
          this.localStorage.setIsWhatsappExist(true);
          this.organizationConfig = response.data;
          this.getOrganizationConfigurations();
        },
        error: (error) => {
          console.error('Error saving WhatsApp config:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save WhatsApp configuration'
          });
          this.savingWhatsApp = false;
        }
      });
    }
  }

  testWhatsAppConnection() {
    this.configService.testWhatsAppConnection({ config: this.whatsappConfig }).subscribe({
      next: (response: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'WhatsApp connection test successful'
        });
      },
      error: (error) => {
        console.error('Error testing WhatsApp connection:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'WhatsApp connection test failed'
        });
      }
    });
  }
}
